import {GROUND_BACKGROUNDS} from '../groundBackgrounds';
import {WIND_GUST_EFFECT} from './windGust';

/**
 * СОЛНЕЧНЫЕ ЗВЕЗДЫ НА ВОДЕ — эффект островной подложки (island_1).
 *
 * Остров лежит в синем море, вокруг поля — бирюзовые лагуны. То в одной лагуне,
 * то в другой на гребне волны вспыхивает солнечная звезда: яркое ядро с четырьмя
 * тонкими лучами, под ним голубоватый отсвет, а рядом по той же волне одна-три
 * искры поменьше (см. utils/effects/biome/sunStars).
 *
 * Мелкие мягкие блики на этой картинке не читаются: море уже усыпано
 * нарисованными белыми крапинами и пеной. Поэтому звезда — крупная, с лучами и
 * вспышкой, и только на темной и бирюзовой воде. Залитый солнцем
 * северо-западный угол моря пропущен: там белое на белых крапинах не видно.
 *
 * Звезды — на воде вокруг поля: над игровыми ячейками их не бывает. На
 * телефоне море в кадре, только когда игрок смотрит на край поля, поэтому у
 * эффекта есть и часть на самом поле: морской бриз. Раз в полминуты по сухой
 * траве острова пробегает светлая рябь — тот же порыв ветра, что на лугах
 * (utils/effects/biome/windGust), только без листьев.
 */
export const SUN_STARS_KIND = 'sunStars';

// Порыв ветра над лугом: бриз острова — его травяные волны
const MEADOW_GUST = WIND_GUST_EFFECT.backgrounds[GROUND_BACKGROUNDS.GREEN_1];

// Вес места на воде: лагуны у самого поля вспыхивают вдвое чаще открытого моря —
// их игрок видит рядом с полем, и вода в них спокойная и темная
const SEA = 1;
const LAGOON = 2;

/**
 * Вода острова: круги картинки пейзажа (доли ее стороны), целиком лежащие на
 * темной или бирюзовой воде — без пены, скал и пляжей. Сверены с картинкой
 * глазами; маска «вода по цвету» здесь врет (синие тени скал), заново по цвету
 * круги не собирать. Круги у самого края картинки (u или v около 0.1 и 0.9)
 * заходят в затемнение края пейзажа: звезды там тусклее.
 */
const ISLAND_1_WATERS = Object.freeze([
  // Лагуны и рифовые заводи у поля
  {u: 0.763, v: 0.248, radius: 0.023, weight: LAGOON}, // бухта на северо-востоке, у самого угла поля
  {u: 0.829, v: 0.382, radius: 0.030, weight: LAGOON}, // восточная лагуна
  {u: 0.617, v: 0.819, radius: 0.028, weight: LAGOON}, // южная бухта
  {u: 0.356, v: 0.821, radius: 0.029, weight: LAGOON}, // юго-западная лагуна
  {u: 0.540, v: 0.164, radius: 0.018, weight: LAGOON}, // северные рифовые заводи
  {u: 0.616, v: 0.180, radius: 0.015, weight: LAGOON},
  {u: 0.658, v: 0.165, radius: 0.015, weight: LAGOON},
  {u: 0.168, v: 0.412, radius: 0.016, weight: LAGOON}, // западная лагуна, бирюзовая часть
  {u: 0.846, v: 0.678, radius: 0.020, weight: LAGOON}, // бухта на юго-востоке, бирюзовая часть
  // Вода у рифов и скал
  {u: 0.862, v: 0.509, radius: 0.023, weight: SEA}, // у восточного рифа
  {u: 0.880, v: 0.682, radius: 0.030, weight: SEA}, // устье юго-восточной бухты (ниже пены у скал)
  {u: 0.516, v: 0.860, radius: 0.012, weight: SEA}, // южный заливчик
  {u: 0.141, v: 0.755, radius: 0.021, weight: SEA}, // у юго-западного рифа
  {u: 0.121, v: 0.705, radius: 0.027, weight: SEA}, // у западного рифа
  {u: 0.173, v: 0.687, radius: 0.013, weight: SEA}, // западная бухточка
  {u: 0.700, v: 0.876, radius: 0.016, weight: SEA}, // у южных рифов
  {u: 0.755, v: 0.859, radius: 0.015, weight: SEA},
  // Открытое темное море
  {u: 0.105, v: 0.606, radius: 0.030, weight: SEA}, // запад
  {u: 0.130, v: 0.420, radius: 0.026, weight: SEA}, // запад, вода с рябью: контраст слабее
  {u: 0.470, v: 0.123, radius: 0.019, weight: SEA}, // север
  {u: 0.613, v: 0.100, radius: 0.040, weight: SEA},
  {u: 0.768, v: 0.100, radius: 0.040, weight: SEA},
  {u: 0.821, v: 0.194, radius: 0.040, weight: SEA}, // северо-восток
  {u: 0.898, v: 0.254, radius: 0.038, weight: SEA}, // восток
  {u: 0.898, v: 0.385, radius: 0.035, weight: SEA},
  {u: 0.898, v: 0.801, radius: 0.040, weight: SEA}, // юго-восток
  {u: 0.624, v: 0.898, radius: 0.040, weight: SEA}, // юг
  {u: 0.326, v: 0.896, radius: 0.040, weight: SEA},
  {u: 0.166, v: 0.898, radius: 0.037, weight: SEA}, // юго-запад
]);

export const SUN_STARS_EFFECT = Object.freeze({
  kind: SUN_STARS_KIND,
  backgrounds: Object.freeze({
    [GROUND_BACKGROUNDS.ISLAND_1]: Object.freeze({
      firstDelayMs: {min: 300, max: 1200},
      // Вспышка — звезда со спутниками на одном месте воды. Пауза — от начала
      // предыдущей вспышки, на все море разом: при таком ритме открытое место
      // вспыхивает в среднем раз в 5–6 секунд, лагуна — раз в 3, а на всем
      // кольце воды живы 3–5 звезд
      intervalMs: {min: 100, max: 200},
      maxGroups: 12,
      waters: ISLAND_1_WATERS,
      // Звезда рождается не у самой кромки круга воды: ее лучам тоже нужна вода
      waterInsetShare: 0.8,
      // Над игровой ячейкой и вплотную к ней звезд нет: не ближе стольких
      // размеров ячейки от ее середины (поле неправильной формы дотягивается
      // до бухты на северо-востоке)
      keepOffCellFactor: 1.2,
      flash: {
        // Вспышка короткая: звезда раскрывается и сворачивается
        lifeMs: {min: 450, max: 800},
        // Размер в начале и в конце вспышки, доля полного
        minScale: 0.3,
        // За вспышку звезда чуть поворачивается (в любую сторону)
        turnDeg: 25,
      },
      star: {
        // Размер кадра звезды: доля стороны картинки пейзажа (звезда привязана
        // к нарисованной воде и растет вместе с ней)…
        sizeShare: {min: 0.010, max: 0.016},
        // …но не мельче и не крупнее стольких размеров ячейки: мелкую не видно
        // среди нарисованных крапин, крупная превращает спокойное море в
        // мишуру. Лучи к концам тают, и видимая звезда — около двух третей
        // кадра: на телефоне это 9–15 точек экрана
        sizeFactor: {min: 0.7, max: 1.1},
        tint: 0xffffff,
        alpha: 1,
      },
      // Голубоватый отсвет под звездой: мягкое пятно. Кадр пятна шире кадра
      // звезды, но видимая его середина — с ядро звезды и основания лучей
      halo: {
        tint: 0xbfe9ff,
        alpha: 0.45,
        scale: 1.3,
      },
      // Искры-спутники на том же гребне волны: загораются одна за другой в
      // стороне от звезды по горизонтали
      companions: {
        count: {min: 1, max: 3},
        staggerMs: {min: 80, max: 200},
        // Меньше и слабее звезды, гаснут быстрее
        scale: 0.5,
        alpha: 0.8,
        lifeShare: 0.8,
        // Отступ от звезды вдоль волны и разброс поперек нее, размеров ячейки
        alongFactor: {min: 0.25, max: 0.65},
        acrossFactor: 0.14,
      },

      /**
       * Морской бриз: числа порыва ветра над лугом, но чаще (остров открыт
       * всем ветрам, порывы идут почти один за другим) и без листьев — на
       * сухом острове ветру нечего нести. Травяные волны катятся по сухой
       * траве середины острова: на пляжах, скалах и воде вокруг их нет
       */
      breeze: Object.freeze({
        ...MEADOW_GUST,
        firstDelayMs: {min: 3000, max: 9000},
        intervalMs: {min: 20000, max: 32000},
        plain: {u: 0.5, v: 0.5, radiusU: 0.27},
        leaves: {...MEADOW_GUST.leaves, count: {min: 0, max: 0}},
        // Краски волны — с сухой травы и песка острова. Земля здесь светлая и
        // пестрая: светлая полоса на ней едва видна, волну выдает тень за
        // гребнем — поэтому и гребень, и тень плотнее, чем на лугу
        waveTints: {light: 0xfff0d6, shade: 0x4a3a26},
        waves: {
          ...MEADOW_GUST.waves,
          alpha: {min: 0.46, max: 0.6},
          shade: {...MEADOW_GUST.waves.shade, alphaShare: 0.45},
        },
      }),
    }),
  }),
});
